export const REFRESH_LOCK_NAME = "progsity-auth-refresh";

type RefreshTask<T> = () => Promise<T>;
type ExclusiveLockRunner = <T>(task: RefreshTask<T>) => Promise<T>;

/**
 * Serialise refresh-cookie rotation across browser tabs.
 *
 * The backend uses one-time refresh tokens. Without a cross-tab lock, two tabs
 * waking at the same time can send the same cookie: one rotates it and the
 * other receives 401, which used to make the UI clear an otherwise valid
 * session. Web Locks keeps those rotations ordered; unsupported browsers still
 * get the in-tab promise deduplication below.
 */
async function withBrowserRefreshLock<T>(task: RefreshTask<T>): Promise<T> {
  if (typeof navigator === "undefined" || !navigator.locks) return task();

  return navigator.locks.request(
    REFRESH_LOCK_NAME,
    { mode: "exclusive" },
    () => task(),
  );
}

/**
 * `EX-TMP03`: any other request that replaces the refresh cookie takes the
 * same lock. Converting a contest account revokes every session and sets a new
 * cookie; a second tab that refreshed with the old cookie in that window got
 * 401, and its sign-out cleared the new cookie too.
 */
export const withAuthRefreshLock = withBrowserRefreshLock;

/** One refresh request per tab at a time, with optional cross-tab locking. */
export function createRefreshCoordinator<T>(
  refresh: RefreshTask<T>,
  withLock: ExclusiveLockRunner = withBrowserRefreshLock,
): RefreshTask<T> {
  let pending: Promise<T> | null = null;

  return () => {
    if (pending) return pending;

    pending = withLock(refresh).finally(() => {
      pending = null;
    });
    return pending;
  };
}
